/* eslint-disable */
import {
  Card,
  Select,
  Spin, Typography
} from 'antd';
import { AxiosError } from 'axios';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import ChartCustom from 'common/components/Chart';
import { getCounterByQuarterV3Service, getKioskCounterByMonthV3Service } from 'common/services/dashboard';
import { formatNumberWithSeparators } from 'common/utils/functions';
import moment from 'moment';

interface NationwideByMonthProps {
  dateRange: string[];
  typeParam: number | undefined;
  kiosGroupId: number[];
  setRefetchFunc: (fn: () => void) => void,
  setRefetchFunc2: (fn: () => void) => void,
}

const NationwideByMonth: React.FC<NationwideByMonthProps> = ({
  dateRange,
  typeParam,
  kiosGroupId,
  setRefetchFunc,
  setRefetchFunc2,
}) => {
  const { t } = useTranslation();
  const [isErrorTimeout, setIsErrorTimeout] = React.useState<boolean>(false);
  const [typeData, setTypeData] = React.useState<string>('month');

  const {
    data: counterByMonthData, isFetching, refetch
  } = useQuery(
    ['getKioskCounterByMonthV3Query'],
    () => getKioskCounterByMonthV3Service({
      from: dateRange[0],
      to: dateRange[1],
      ...(typeParam && { type: typeParam }),
      ...(kiosGroupId.join(',') && { kioskGroupIds: kiosGroupId.join(',') }),
    }),
    {
      onError: (err: AxiosError) => {
        if (err.response?.status === 408 || err.response?.status === 504) {
          setIsErrorTimeout(true);
        }
      },
      onSuccess: () => {
        setIsErrorTimeout(false);
      }
    }
  );

  const {
    data: counterByQuarterData,
    isFetching: isFetchingCounterByQuarter,
    refetch: refetchCounterByQuarter
  } = useQuery(
    ['getCounterByQuarterV3Query'],
    () => getCounterByQuarterV3Service({
      from: dateRange[0],
      to: dateRange[1],
      ...(typeParam && { type: typeParam }),
      ...(kiosGroupId.join(',') && { kioskGroupIds: kiosGroupId.join(',') }),
    }),
    {
      onError: (err: AxiosError) => {
        if (err.response?.status === 408 || err.response?.status === 504) {
          setIsErrorTimeout(true);
        }
      },
      onSuccess: () => {
        setIsErrorTimeout(false);
      }
    }
  );

  const data = useMemo(() => ({
    labels: counterByMonthData?.map(
      // (item) => `T${item.yearMonth.split('-')[1].replace(/^0/, '')}`
      (item) => `T${moment(item.yearMonth, 'YYYY-MM').format('M/YY')}`
    ) || [],
    datasets: [
      {
        data: counterByMonthData?.map((item) => item.total) || [],
        borderColor: '#b93c7f',
        fill: true,
        tension: 0.3,
        backgroundColor: (context: any) => {
          const { chart } = context;
          const { ctx, chartArea } = chart;

          if (!chartArea) {
            return null;
          }

          const gradient = ctx.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom
          );
          gradient.addColorStop(0, 'rgba(185, 60, 127, 0.4)'); // top (near line)
          gradient.addColorStop(1, 'rgba(185, 60, 127, 0)'); // bottom (transparent)
          return gradient;
        },
      },
    ],
  }), [counterByMonthData]);

  const dataQuarter = useMemo(() => ({
    labels: counterByQuarterData?.map((item) => `Q${item.quarter}/${item.year.toString().slice(-2)}`) || [],
    datasets: [
      {
        data: counterByQuarterData?.map((item) => item.total) || [],
        borderColor: '#b93c7f',
        fill: true,
        tension: 0.3,
        backgroundColor: (context: any) => {
          const { chart } = context;
          const { ctx, chartArea } = chart;

          if (!chartArea) {
            return null;
          }

          const gradient = ctx.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom
          );
          gradient.addColorStop(0, 'rgba(185, 60, 127, 0.4)'); // top (near line)
          gradient.addColorStop(1, 'rgba(185, 60, 127, 0)'); // bottom (transparent)
          return gradient;
        },
      },
    ],
  }), [counterByMonthData]);

  const options = useMemo(() => {
    return {
      layout: {
        padding: {
          top: 25
        }
      },
      plugins: {
        legend: {
          display: false,
        },
        datalabels: {
          display: (context: any) => Number(counterByMonthData?.length) > 12 ? (context.dataIndex % 2 === 1) : undefined,
          color: '#333',
          anchor: 'end',
          align: 'top',
          formatter: (value: any) => formatNumberWithSeparators(value),
        },
        tooltip: {
          enabled: true, // still show value on hover
        },
      },
      responsive: true,
      scales: {
        y: {
          title: {
            display: true,
            text: '',
          },
          grid: {
            display: true, // hides horizontal grid lines
            borderDash: [5, 5],
          },
        },
        x: {
          grid: {
            display: false, // hides horizontal grid lines
            // borderDash: [5, 5],
          },
        }
      },
    };
  }, [counterByMonthData]);

  useEffect(() => {
    setRefetchFunc(() => refetch);
    setRefetchFunc2(() => refetchCounterByQuarter);
  }, [refetch, refetchCounterByQuarter]);

  return (
    <Spin spinning={isFetching || isFetchingCounterByQuarter}>
      <Card type="inner">
        <div className="p-home_chart">
          <h1
            style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#3D424F',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {t('dashboard2025.ratingByMonth2')}
            <Select
              defaultValue={'month'}
              size='large'
              options={[
                { value: 'month', label: t('system.month') },
                { value: 'quarter', label: t('system.quarterYear') },
              ]}
              onChange={(value) => setTypeData(value)} />
          </h1>
          {isErrorTimeout ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%'
              }}
            >
              <Typography.Title level={4}>
                {t('message.errorTimeout')}
              </Typography.Title>
            </div>
          ) : (
            <ChartCustom
              data={typeData === 'month' ? data : dataQuarter}
              type="line"
              options={options}
              plugins={[ChartDataLabels]}
            />
          )}
        </div>
        <div
          className="u-mt-20"
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 4,
              border: '1px solid #D7DAE0',
              padding: 4,
              width: 'fit-content',
              borderRadius: '6px',
            }}
          >
            <span
              style={{
                width: '14px',
                height: '14px',
                borderRadius: '4px',
                backgroundColor: '#b93c7f'
              }}
            />
            <div style={{ textAlign: 'center', fontSize: 14 }}>{t('dashboard2025.rating')}</div>
          </div>
        </div>
      </Card>
    </Spin>
  );
};
export default NationwideByMonth;